import { ShieldCheck, CheckCircle, DollarSign, Home, ThumbsUp, Ruler, CalendarClock, Hammer, Users, Wrench } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/foundations.js';



export default function FoundationPage() {
  const service = {
    title: 'Foundation Services',
    image: '/hero/best-foundations-remodeling-services-in-los-angeles.webp',
    description: 'Foundation repair & new construction by Modern Build. Reliable, code-compliant results. Free estimate: (323) 300-4130',
    link: '/services/foundations',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

  
      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Foundations Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Foundation Repair & Construction | Free Estimate | Modern Build</title>
        <meta name="title" content="Foundation Repair & Construction | Free Estimate | Modern Build" />
        <meta name="description" content="Foundation repair & new construction by Modern Build. Reliable, code-compliant results. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="foundation repair, foundation construction, los angeles foundation contractor, structural reinforcement, concrete foundation, slab foundation, crawl space repair, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/foundations" />

        <meta property="og:title" content="Foundation Repair & Construction | Free Estimate | Modern Build" />
        <meta property="og:description" content="PFoundation repair & new construction by Modern Build. Reliable, code-compliant results. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-foundations-remodeling-services-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/foundations" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Foundation Repair & Construction | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Foundation repair & new construction by Modern Build. Reliable, code-compliant results. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-foundations-remodeling-services-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Foundation Repair & Construction | Free Estimate | Modern Build',
                description: 'Modern Build offers expert foundation repair and new construction services in Los Angeles. From inspections to seismic upgrades, we ensure long-term stability for your home.',
                slug: '/services/foundations'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="foundations" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="FOUNDATION SERVICES"
        title="Stability Starts Below the Surface"
        background="white"
        description="Modern Build provides comprehensive foundation solutions for homes across Los Angeles — from repairs and retrofitting to full foundation replacement and new construction."
        extraText="We combine engineering precision with quality craftsmanship to protect your home’s integrity."
        phone="(323) 300-4130"
        image1="/split/foundation-repair-los-angeles.webp"
        image2="/split/foundation-construction-los-angeles.webp"
      />

      <Benefits
        title="What’s Included in Our Foundation Work?"
        subtitle="Engineered for strength, built to last"
        items={[
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Crack Repair & Structural Reinforcement",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Foundation Leveling & Replacement",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Engineering Plans & Permits",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Soil Grading & Drainage Solutions",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Slab, Crawl Space & Raised Foundations",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Retaining Walls & Structural Footings",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Financing Available",
            href: "/financing",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Bonded & Insured Installers",
            href: "/about-us",
          },
        ]}
      />


      <ContentBlock
        subtitle="LOS ANGELES FOUNDATION EXPERTS"
        title="Protect Your Home’s Structural Integrity"
        description="Whether your home needs reinforcement, crack repair, or a completely new foundation, Modern Build delivers solutions that meet city code and stand the test of time."
        extraText="Our experienced team handles assessments, planning, permits, and construction — ensuring quality from the ground up."
        buttonText="Request a Free Foundation Evaluation"
        background="white"
      />

      <ProcessRoadmap
        title="Our Foundation Repair & Build Process"
        subtitle="Proven process, lasting results"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free On-Site Evaluation",
            description: "We assess the current condition of your foundation and identify any structural concerns."
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Engineering & Permitting",
            description: "Our team develops a structural plan that complies with city codes and soil conditions."
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Construction & Reinforcement",
            description: "We execute repairs, reinforcements, or new foundation work using top-grade materials."
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Inspection & Sign-Off",
            description: "We coordinate final inspections and provide documentation for your records."
          }
        ]}
      />

      <Split
        subtitle="BUILT TO LAST"
        title="Start from a Strong Foundation"
        background="white"
        description="Your home’s strength begins with its foundation. Whether you’re repairing damage or starting new, Modern Build delivers structural excellence with every project."
        extraText="Let us help you ensure long-term stability and safety."
        phone="(323) 300-4130"
        image1="/split/structural-foundation-los-angeles.webp"
        image2="/split/new-foundation-build-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Get Your Foundation Checked"
        subtitle="Free Evaluation + Project Financing Available"
        buttonText="Book Your Inspection"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="foundations" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}